// Portable at-rest encryption for locally-stored secrets (nsec today; the
// BitcoinVN key + synced app-data blob when this engine is ported to zap.tips).
//
// Design (see Vault): a secret is NEVER persisted in plaintext. It is sealed
// into a `SecretBox` envelope using AES-GCM. Two key sources:
//   - 'device'     — a non-extractable AES-GCM CryptoKey the frontend persists
//                    in IndexedDB. Transparent (no passphrase); defeats the
//                    plaintext-localStorage-scraping threat that afflicts other
//                    web signers.
//   - 'passphrase' — AES-GCM key derived from a user passphrase via PBKDF2
//                    (210k, SHA-256). Strongest: survives full localStorage +
//                    IndexedDB theft. Optional, user opt-in.
//
// This module is pure crypto over WebCrypto (`globalThis.crypto.subtle`), so it
// runs identically in the browser and in node/vitest. It performs NO storage
// I/O — the frontend owns IndexedDB (device key) and localStorage (the box).

const PBKDF2_ITERATIONS = 210_000;
const PBKDF2_HASH = 'SHA-256';
const AES_ALGO = 'AES-GCM';
const AES_LENGTH = 256;
const IV_BYTES = 12;
const SALT_BYTES = 16;

export type SecretBoxMode = 'device' | 'passphrase';

/** Serializable envelope stored in localStorage. All binary fields base64. */
export interface SecretBox {
  readonly v: 1;
  readonly mode: SecretBoxMode;
  readonly iv: string;
  readonly ct: string;
  /** Present only for `mode: 'passphrase'` — the PBKDF2 salt. */
  readonly salt?: string;
}

function subtle(): SubtleCrypto {
  const c = (globalThis as { crypto?: Crypto }).crypto;
  if (!c?.subtle) throw new Error('WebCrypto unavailable in this environment');
  return c.subtle;
}

function randomBytes(n: number): Uint8Array {
  const b = new Uint8Array(n);
  globalThis.crypto.getRandomValues(b);
  return b;
}

function toB64(bytes: Uint8Array): string {
  let s = '';
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i] ?? 0);
  return btoa(s);
}

function fromB64(b64: string): Uint8Array {
  const s = atob(b64);
  const out = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) out[i] = s.charCodeAt(i);
  return out;
}

/**
 * Generate a fresh non-extractable AES-GCM key for device-key mode. The
 * frontend persists the returned CryptoKey object directly in IndexedDB
 * (structured-clone stores non-extractable keys without ever exposing bytes).
 */
export async function generateDeviceKey(): Promise<CryptoKey> {
  return subtle().generateKey({ name: AES_ALGO, length: AES_LENGTH }, false, [
    'encrypt',
    'decrypt',
  ]);
}

/** Derive an AES-GCM key from a passphrase + salt (PBKDF2, 210k, SHA-256). */
export async function deriveKeyFromPassphrase(
  passphrase: string,
  salt: Uint8Array,
): Promise<CryptoKey> {
  const material = await subtle().importKey(
    'raw',
    new TextEncoder().encode(passphrase),
    'PBKDF2',
    false,
    ['deriveKey'],
  );
  return subtle().deriveKey(
    { name: 'PBKDF2', salt: salt as unknown as BufferSource, iterations: PBKDF2_ITERATIONS, hash: PBKDF2_HASH },
    material,
    { name: AES_ALGO, length: AES_LENGTH },
    false,
    ['encrypt', 'decrypt'],
  );
}

/** Seal plaintext with a device CryptoKey → `mode: 'device'` box. */
export async function sealWithDeviceKey(plaintext: string, key: CryptoKey): Promise<SecretBox> {
  const iv = randomBytes(IV_BYTES);
  const ct = await subtle().encrypt(
    { name: AES_ALGO, iv: iv as unknown as BufferSource },
    key,
    new TextEncoder().encode(plaintext),
  );
  return { v: 1, mode: 'device', iv: toB64(iv), ct: toB64(new Uint8Array(ct)) };
}

/** Seal plaintext under a passphrase (fresh salt) → `mode: 'passphrase'` box. */
export async function sealWithPassphrase(plaintext: string, passphrase: string): Promise<SecretBox> {
  const salt = randomBytes(SALT_BYTES);
  const key = await deriveKeyFromPassphrase(passphrase, salt);
  const iv = randomBytes(IV_BYTES);
  const ct = await subtle().encrypt(
    { name: AES_ALGO, iv: iv as unknown as BufferSource },
    key,
    new TextEncoder().encode(plaintext),
  );
  return { v: 1, mode: 'passphrase', iv: toB64(iv), ct: toB64(new Uint8Array(ct)), salt: toB64(salt) };
}

/** Open a `mode: 'device'` box with the device CryptoKey. Throws on tamper/wrong key. */
export async function openWithDeviceKey(box: SecretBox, key: CryptoKey): Promise<string> {
  if (box.mode !== 'device') throw new Error('SecretBox is not device-mode');
  const pt = await subtle().decrypt(
    { name: AES_ALGO, iv: fromB64(box.iv) as unknown as BufferSource },
    key,
    fromB64(box.ct) as unknown as BufferSource,
  );
  return new TextDecoder().decode(pt);
}

/** Open a `mode: 'passphrase'` box. Throws on wrong passphrase or tamper. */
export async function openWithPassphrase(box: SecretBox, passphrase: string): Promise<string> {
  if (box.mode !== 'passphrase' || !box.salt) throw new Error('SecretBox is not passphrase-mode');
  const key = await deriveKeyFromPassphrase(passphrase, fromB64(box.salt));
  const pt = await subtle().decrypt(
    { name: AES_ALGO, iv: fromB64(box.iv) as unknown as BufferSource },
    key,
    fromB64(box.ct) as unknown as BufferSource,
  );
  return new TextDecoder().decode(pt);
}

/** Runtime guard: is `value` a plausible SecretBox envelope (vs a legacy plaintext string)? */
export function isSecretBox(value: unknown): value is SecretBox {
  return (
    typeof value === 'object' &&
    value !== null &&
    (value as SecretBox).v === 1 &&
    ((value as SecretBox).mode === 'device' || (value as SecretBox).mode === 'passphrase') &&
    typeof (value as SecretBox).iv === 'string' &&
    typeof (value as SecretBox).ct === 'string'
  );
}
